import React, { useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import './NewAuction.css';
import { HerracContext } from '../../../App';
import axiosInstance from '../../axiosInstance';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Swal from 'sweetalert2';
import { differenceInMinutes, differenceInHours, differenceInDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import AutomobileForm from '../../subcategories/AutomobileForm';
import MobilePhoneForm from '../../subcategories/MobilePhoneForm';
import LaptopForm from '../../subcategories/LaptopForm';
import DesktopComputerForm from '../../subcategories/DesktopComputerForm';
import TelevisionForm from '../../subcategories/TelevisionForm';
import Apartment from '../../subcategories/Apartment';
import SparePartsForm from '../../subcategories/SparePartsForm';
import TruckForm from '../../subcategories/TruckForm';
import LandForm from '../../subcategories/LandForm';
import OfficeForm from '../../subcategories/OfficeForm';
import MotorcycleForm from '../../subcategories/MotorcycleForm';
import WaterTransportForm from '../../subcategories/WaterTransportForm';
import BusForm from '../../subcategories/BusForm';
import ConstructionEquipmentForm from '../../subcategories/ConstructionEquipmentForm';
import ClothingForm from '../../subcategories/ClothingForm';
import AccessoriesForm from '../../subcategories/AccessoriesForm';
import WatchesJewelryForm from '../../subcategories/WatchesJewelryForm';
import BirdsForm from '../../subcategories/BirdsForm';
import AquariumFishForm from '../../subcategories/AquariumFishForm';
import RabbitForm from '../../subcategories/RabbitForm';
import DogForm from '../../subcategories/DogForm';
import CatForm from '../../subcategories/CatForm';
import HorseForm from '../../subcategories/HorseForm';
import FarmAnimalsForm from '../../subcategories/FarmAnimalsForm';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import imageCompression from 'browser-image-compression';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

function NewAuction() {
  const { categoriesData, citiesData, user, balance, getUserBalance } = useContext(HerracContext);
  const navigate = useNavigate();
  const prevSubCategoryRef = useRef(null);
  const imageInputRef = useRef(null);

  // ============ STATE ============
  const [selectedDate, setSelectedDate] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem('darkMode') === 'true';
  });
  const [showPreview, setShowPreview] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('');
  const [marketPrices, setMarketPrices] = useState(null);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [isLoadingPrices, setIsLoadingPrices] = useState(false);
  const [imageProcessing, setImageProcessing] = useState(false);
  const [shake, setShake] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageUploadStatus, setImageUploadStatus] = useState('');

  const [formValues, setFormValues] = useState({
    userId: user?.userId,
    subCategoryId: '',
    name: '',
    cityId: '',
    price: '',
    description: '',
    endDate: '',
    images: [],
    imageUrls: [],
    additionalAttributes: {},
  });

  const [formValidation, setFormValidation] = useState({
    name: false,
    cityId: false,
    price: false,
    description: false,
    endDate: false,
    images: false,
    subCategoryId: false,
  });

  const [timeDifferences, setTimeDifferences] = useState({
    days: 0,
    hours: 0,
    minutes: 0
  });

  const [subCategoryAttributes, setSubCategoryAttributes] = useState([]);
  const [attributeValues, setAttributeValues] = useState([]);

  // ============ UTILITY FUNCTIONS ============

  const calculateProgress = useCallback(() => {
    const totalFields = Object.keys(formValidation).length;
    const completedFields = Object.values(formValidation).filter(Boolean).length;
    return Math.round((completedFields / totalFields) * 100);
  }, [formValidation]);

  const validateField = (name, value) => {
    let isValid = false;

    switch (name) {
      case 'name':
        isValid = value && value.length >= 5;
        break;
      case 'price':
        isValid = value && parseFloat(value) > 0;
        break;
      case 'description':
        isValid = value && value.length >= 20;
        break;
      case 'cityId':
      case 'subCategoryId':
        isValid = value !== '';
        break;
      case 'endDate':
        isValid = value !== '';
        break;
      case 'images':
        isValid = Array.isArray(value) && value.length > 0;
        break;
      default:
        isValid = true;
    }

    setFormValidation(prev => ({ ...prev, [name]: isValid }));
    return isValid;
  };

  // ============ EFFECTS ============

  useEffect(() => {
    document.body.classList.toggle('dark-mode', isDarkMode);
    localStorage.setItem('darkMode', isDarkMode);
  }, [isDarkMode]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (formValues.name || formValues.description) {
        try {
          const draftData = {
            ...formValues,
            images: [],
            imageUrls: formValues.imageUrls,
            savedAt: new Date().toISOString()
          };

          localStorage.setItem('draftAuction', JSON.stringify(draftData));
          const now = new Date();
          setAutoSaveStatus(`${now.getHours()}:${now.getMinutes().toString().padStart(2, '0')}`);

          toast.info('📝 Qaralama avtomatik yadda saxlanıldı', {
            autoClose: 2000,
            position: 'bottom-right'
          });
        } catch (error) {
          console.error('localStorage xətası:', error);
          toast.warning('Yadda saxlama xətası.');
        }
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [formValues]);

  useEffect(() => {
    try {
      const draft = localStorage.getItem('draftAuction');
      if (draft) {
        const parsedDraft = JSON.parse(draft);
        const draftAge = Date.now() - new Date(parsedDraft.savedAt).getTime();
        const draftAgeHours = draftAge / (1000 * 60 * 60);

        if (draftAgeHours > 24) {
          localStorage.removeItem('draftAuction');
          return;
        }

        Swal.fire({
          title: '📂 Qaralama Tapıldı',
          html: `
            <p>Yarımçıq qalmış elanınız var.</p>
            <p style="font-size: 0.875rem; color: #6b7280;">
              Son yadda saxlanma: ${new Date(parsedDraft.savedAt).toLocaleString('az-AZ')}
            </p>
          `,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Davam et',
          cancelButtonText: 'Yeni başla',
          confirmButtonColor: '#6366f1',
          cancelButtonColor: '#ef4444'
        }).then((result) => {
          if (result.isConfirmed) {
            setFormValues({
              ...parsedDraft,
              images: [],
              userId: user?.userId
            });

            Object.keys(formValidation).forEach(key => {
              validateField(key, parsedDraft[key]);
            });

            toast.success('📂 Qaralama yükləndi. Şəkilləri yenidən əlavə edin.');
          } else {
            localStorage.removeItem('draftAuction');
          }
        });
      }
    } catch (error) {
      console.error('Draft yüklənmə xətası:', error);
      localStorage.removeItem('draftAuction');
    }
  }, []);

  useEffect(() => {
    try {
      const templates = JSON.parse(localStorage.getItem('auctionTemplates') || '[]');
      setSavedTemplates(templates);
    } catch (error) {
      console.error('Şablon yüklənmə xətası:', error);
      setSavedTemplates([]);
    }
  }, []);

  useEffect(() => {
    if (user) {
      getUserBalance();
    }
  }, [user, getUserBalance]);

  useEffect(() => {
    if (prevSubCategoryRef.current && prevSubCategoryRef.current !== formValues.subCategoryId) {
      setFormValues(prev => ({
        ...prev,
        additionalAttributes: {}
      }));
    }
    prevSubCategoryRef.current = formValues.subCategoryId;
  }, [formValues.subCategoryId]);

  useEffect(() => {
    if (!formValues.subCategoryId) {
      setSubCategoryAttributes([]);
      return;
    }

    const fetchAttributes = async () => {
      try {
        const response = await axiosInstance.get(
          `/Products/subcategoryattributes/${formValues.subCategoryId}`
        );
        setSubCategoryAttributes(response.data);
        setAttributeValues([]);
      } catch (error) {
        console.error('Atribut yüklənmə xətası:', error);
        setSubCategoryAttributes([]);
      }
    };

    fetchAttributes();
  }, [formValues.subCategoryId]);

  useEffect(() => {
    const selectedAttribute = subCategoryAttributes.find(
      (attr) => attr.attributeId === parseInt(formValues.attribute)
    );
    setAttributeValues(selectedAttribute ? selectedAttribute.attributeValues : []);
  }, [formValues.attribute, subCategoryAttributes]);

  // ============ INPUT HANDLERS ============

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const handleDateChange = (date) => {
    if (!date) return;

    setSelectedDate(date);

    const currentDate = new Date();
    const minutesDifference = differenceInMinutes(date, currentDate);
    const hoursDifference = differenceInHours(date, currentDate);
    const daysDifference = differenceInDays(date, currentDate);

    setTimeDifferences({
      days: daysDifference,
      hours: hoursDifference % 24,
      minutes: minutesDifference % 60
    });

    const formattedEndDate = `${date.getDate().toString().padStart(2, "0")}.${(date.getMonth() + 1).toString().padStart(2, "0")}.${date.getFullYear()} ${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}:${date.getSeconds().toString().padStart(2, "0")}`;

    setFormValues(prev => ({ ...prev, endDate: formattedEndDate }));
    validateField('endDate', formattedEndDate);
  };

  const filterPassedTime = (time) => {
    const now = new Date();
    const selected = selectedDate || now;

    if (selected.toDateString() === now.toDateString()) {
      return time.getTime() >= now.getTime();
    }
    return true;
  };

  const minDate = new Date();
  const maxDate = new Date(new Date().setDate(new Date().getDate() + 30));

  // ============ IMAGE HANDLERS ============

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files || []);

    console.log('🖼️ Image selection started:', {
      fileCount: files.length,
      currentImages: formValues.images.length,
      maxAllowed: 8
    });

    if (files.length === 0) {
      console.warn('⚠️ No files selected');
      return;
    }

    if (files.length + formValues.images.length > 8) {
      toast.error('Maksimum 8 şəkil yükləyə bilərsiniz!');
      setImageUploadStatus('❌ Çox sayda şəkil');
      return;
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const invalidFiles = files.filter(file => !validTypes.includes(file.type));

    if (invalidFiles.length > 0) {
      console.warn('⚠️ Invalid file types:', invalidFiles.map(f => f.type));
      toast.error('Yalnız JPG, PNG və WebP formatları dəstəklənir!');
      setImageUploadStatus('❌ Səhv format');
      return;
    }

    setImageProcessing(true);
    setImageUploadStatus('⏳ Şəkillər optimallaşdırılır...');
    const compressedImages = [];
    const imageUrls = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        console.log(`📷 Compressing image ${i + 1}/${files.length}:`, file.name, file.size);

        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
          fileType: 'image/jpeg'
        };

        try {
          const compressedFile = await imageCompression(file, options);
          console.log(`✅ Compressed ${file.name}:`, file.size, '→', compressedFile.size);

          compressedImages.push(compressedFile);

          const objectUrl = URL.createObjectURL(compressedFile);
          console.log(`🔗 Object URL created:`, objectUrl);
          imageUrls.push(objectUrl);

        } catch (compressionError) {
          console.error(`❌ Compression error for ${file.name}:`, compressionError);
          throw compressionError;
        }
      }

      console.log('✅ All images compressed:', {
        count: compressedImages.length,
        urls: imageUrls.length
      });

      setFormValues(prev => {
        const newState = {
          ...prev,
          images: [...prev.images, ...compressedImages],
          imageUrls: [...prev.imageUrls, ...imageUrls]
        };
        console.log('📊 Form values updated:', {
          totalImages: newState.images.length,
          totalUrls: newState.imageUrls.length
        });
        return newState;
      });

      validateField('images', [...formValues.images, ...compressedImages]);

      setImageUploadStatus(`✅ ${compressedImages.length} şəkil yükləndi`);
      toast.success(`✅ ${compressedImages.length} şəkil yükləndi və optimallaşdırıldı!`);

    } catch (error) {
      console.error('❌ Image compression error:', error);
      setImageUploadStatus('❌ Xəta');
      toast.error('Şəkil yüklənməsində xəta baş verdi');
    } finally {
      setImageProcessing(false);
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    console.log('🎯 Image drag end:', {
      from: result.source.index,
      to: result.destination.index
    });

    const items = Array.from(formValues.images);
    const urls = Array.from(formValues.imageUrls);

    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const [reorderedUrl] = urls.splice(result.source.index, 1);
    urls.splice(result.destination.index, 0, reorderedUrl);

    setFormValues(prev => ({ ...prev, images: items, imageUrls: urls }));
    toast.success('📸 Şəkil sırası dəyişdirildi', { autoClose: 1000 });
  };

  const removeImage = (index) => {
    console.log('🗑️ Removing image at index:', index);

    const urlToRevoke = formValues.imageUrls[index];
    console.log('🔗 Revoking URL:', urlToRevoke);

    if (urlToRevoke && urlToRevoke.startsWith('blob:')) {
      URL.revokeObjectURL(urlToRevoke);
    }

    const newImages = formValues.images.filter((_, i) => i !== index);
    const newUrls = formValues.imageUrls.filter((_, i) => i !== index);

    setFormValues(prev => ({ ...prev, images: newImages, imageUrls: newUrls }));
    validateField('images', newImages);

    console.log('✅ Image removed:', {
      remainingImages: newImages.length,
      remainingUrls: newUrls.length
    });

    toast.info('🗑️ Şəkil silindi', { autoClose: 1000 });
  };

  // ============ MARKET PRICES ============

  const fetchMarketPrices = async () => {
    if (!formValues.subCategoryId) {
      toast.warning('Əvvəlcə kateqoriya seçin');
      return;
    }

    setIsLoadingPrices(true);
    try {
      const response = await axiosInstance.get(
        `/MarketPrices/${formValues.subCategoryId}`
      );
      setMarketPrices(response.data);

      Swal.fire({
        title: '💰 Bazar Qiymətləri',
        html: `
          <div style="text-align: left; padding: 20px; background: #f9fafb; border-radius: 12px;">
            <div style="margin-bottom: 12px;">
              <strong style="color: #6b7280;">Minimum:</strong>
              <span style="color: #10b981; font-size: 1.125rem; font-weight: 600;">${response.data.minPrice} AZN</span>
            </div>
            <div style="margin-bottom: 12px;">
              <strong style="color: #6b7280;">Ortalama:</strong>
              <span style="color: #6366f1; font-size: 1.125rem; font-weight: 600;">${response.data.averagePrice} AZN</span>
            </div>
            <div>
              <strong style="color: #6b7280;">Maksimum:</strong>
              <span style="color: #f59e0b; font-size: 1.125rem; font-weight: 600;">${response.data.maxPrice} AZN</span>
            </div>
          </div>
        `,
        icon: 'info',
        confirmButtonText: 'Bağla',
        confirmButtonColor: '#6366f1'
      });
    } catch (error) {
      console.error('Bazar qiymətləri xətası:', error);
      toast.error('Bazar qiymətləri yüklənə bilmədi');
    } finally {
      setIsLoadingPrices(false);
    }
  };

  // ============ TEMPLATE FUNCTIONS ============

  const saveAsTemplate = () => {
    if (!formValues.name) {
      toast.warning('Şablon yadda saxlamaq üçün ən azı ad daxil edin');
      return;
    }

    Swal.fire({
      title: '💾 Şablon Saxla',
      input: 'text',
      inputLabel: 'Şablon adı',
      inputValue: `Şablon - ${formValues.name}`,
      showCancelButton: true,
      confirmButtonText: 'Saxla',
      cancelButtonText: 'Ləğv et',
      confirmButtonColor: '#6366f1',
      inputValidator: (value) => {
        if (!value) {
          return 'Şablon adı daxil edin!';
        }
      }
    }).then((result) => {
      if (result.isConfirmed) {
        const template = {
          id: Date.now(),
          name: result.value,
          data: {
            ...formValues,
            images: [],
            imageUrls: []
          },
          createdAt: new Date().toISOString()
        };

        const templates = JSON.parse(localStorage.getItem('auctionTemplates') || '[]');
        templates.push(template);
        localStorage.setItem('auctionTemplates', JSON.stringify(templates));
        setSavedTemplates(templates);

        toast.success('💾 Şablon uğurla yadda saxlanıldı!');
      }
    });
  };

  const loadTemplate = (templateId) => {
    const templates = JSON.parse(localStorage.getItem('auctionTemplates') || '[]');
    const template = templates.find(t => t.id === templateId);

    if (template) {
      Swal.fire({
        title: '📂 Şablon Yüklə',
        text: 'Cari formunuz əvəz ediləcək. Davam etmək istəyirsiniz?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Bəli, yüklə',
        cancelButtonText: 'Xeyr',
        confirmButtonColor: '#6366f1',
        cancelButtonColor: '#ef4444'
      }).then((result) => {
        if (result.isConfirmed) {
          setFormValues({
            ...template.data,
            userId: user?.userId,
            images: [],
            imageUrls: []
          });

          Object.keys(formValidation).forEach(key => {
            validateField(key, template.data[key]);
          });

          toast.success('📂 Şablon yükləndi!');
        }
      });
    }
  };

  const deleteTemplate = (templateId, e) => {
    e.stopPropagation();

    Swal.fire({
      title: '🗑️ Şablonu Sil',
      text: 'Bu şablonu silmək istədiyinizdən əminsiniz?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Bəli, sil',
      cancelButtonText: 'Xeyr',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280'
    }).then((result) => {
      if (result.isConfirmed) {
        const templates = savedTemplates.filter(t => t.id !== templateId);
        localStorage.setItem('auctionTemplates', JSON.stringify(templates));
        setSavedTemplates(templates);
        toast.success('🗑️ Şablon silindi');
      }
    });
  };

  // ============ ADDITIONAL ATTRIBUTES ============

  const setAdditionalAttributes = useCallback((attributes) => {
    setFormValues(prev => ({
      ...prev,
      additionalAttributes: attributes
    }));
  }, []);

  // ============ SUBCATEGORY FORM ============

  const getSubcategoryForm = useCallback(() => {
    if (!formValues.subCategoryId) return null;

    const commonProps = {
      additionalAttributes: formValues.additionalAttributes,
      setAdditionalAttributes: setAdditionalAttributes,
      subCategoryId: formValues.subCategoryId,
      formValues: formValues.additionalAttributes,
      setFormValues: setAdditionalAttributes,
      onChange: setAdditionalAttributes
    };

    switch (formValues.subCategoryId) {
      case "1":
        return <AutomobileForm {...commonProps} />;
      case "7":
        return <MobilePhoneForm {...commonProps} />;
      case "8":
        return <LaptopForm {...commonProps} />;
      case "9":
        return <DesktopComputerForm {...commonProps} />;
      case "10":
        return <TelevisionForm {...commonProps} />;
      case "400":
        return <SparePartsForm {...commonProps} />;
      case "2":
        return <TruckForm {...commonProps} />;
      case "20":
        return <Apartment {...commonProps} />;
      case "30":
        return <LandForm {...commonProps} />;
      case "40":
        return <OfficeForm {...commonProps} />;
      case "3":
        return <MotorcycleForm {...commonProps} />;
      case "4":
        return <WaterTransportForm {...commonProps} />;
      case "5":
        return <BusForm {...commonProps} />;
      case "6":
        return <ConstructionEquipmentForm {...commonProps} />;
      case "100":
        return <ClothingForm {...commonProps} />;
      case "110":
        return <AccessoriesForm {...commonProps} />;
      case "14":
        return <WatchesJewelryForm {...commonProps} />;
      case "180":
        return <BirdsForm {...commonProps} />;
      case "170":
        return <AquariumFishForm {...commonProps} />;
      case "190":
        return <RabbitForm {...commonProps} />;
      case "140":
        return <DogForm {...commonProps} />;
      case "150":
        return <CatForm {...commonProps} />;
      case "160":
        return <HorseForm {...commonProps} />;
      case "200":
        return <FarmAnimalsForm {...commonProps} />;
      default:
        return null;
    }
  }, [formValues.subCategoryId, formValues.additionalAttributes, setAdditionalAttributes]);

  const memoizedSubcategoryForm = useMemo(() => getSubcategoryForm(), [getSubcategoryForm]);

  // ============ API URL MAPPER ============

  const getApiUrl = (subCategoryId) => {
    const apiUrls = {
      "1": "/Automobile",
      "7": "/MobilePhone",
      "8": "/Laptop",
      "9": "/DesktopComputer",
      "10": "/Television",
      "400": "/SpareParts",
      "2": "/Truck",
      "20": "/Apartment",
      "30": "/Land",
      "40": "/Office",
      "3": "/Motorcycle",
      "4": "/WaterTransport",
      "5": "/Bus",
      "6": "/ConstructionEquipment",
      "100": "/Clothing",
      "110": "/Accessories",
      "14": "/WatchesJewelry",
      "180": "/Birds",
      "170": "/AquariumFish",
      "190": "/Rabbit",
      "140": "/Dog",
      "150": "/Cat",
      "160": "/Horse",
      "200": "/FarmAnimals"
    };

    return apiUrls[subCategoryId];
  };

  // ============ FORM SUBMISSION - IMPROVED ============

  const newAuctionPost = async (e) => {
    e.preventDefault();

    if (!user) {
      navigate('/giris');
      return;
    }

    setIsSubmitting(true);

    // Validation
    const requiredFields = ["name", "price", "cityId", "endDate", "images", "description"];
    const hasEmptyRequiredField = requiredFields.some((field) => {
      const value = formValues[field];
      return (
        value === undefined ||
        value === null ||
        value === "" ||
        (typeof value === "string" && value.trim() === "") ||
        (Array.isArray(value) && value.length === 0)
      );
    });

    if (hasEmptyRequiredField) {
      Swal.fire({
        icon: 'error',
        title: 'Xəta!',
        text: '* ilə qeyd olunan bütün sahələri doldurun!',
        confirmButtonColor: '#ef4444'
      });
      setIsSubmitting(false);
      return;
    }

    // Check additional attributes
    const isAdditionalFieldMissing = Object.entries(formValues.additionalAttributes || {}).some(
      ([key, value]) =>
        value === undefined ||
        value === null ||
        value === "" ||
        (typeof value === "string" && value.trim() === "") ||
        (Array.isArray(value) && value.length === 0)
    );

    if (isAdditionalFieldMissing) {
      Swal.fire({
        icon: 'error',
        title: 'Xəta!',
        text: 'Bütün əlavə sahələr doldurulmalıdır!',
        confirmButtonColor: '#ef4444'
      });
      setIsSubmitting(false);
      return;
    }

    const apiPath = getApiUrl(formValues.subCategoryId);

    if (!apiPath) {
      Swal.fire({
        icon: 'error',
        title: 'Xəta!',
        text: 'Seçilmiş kateqoriya üçün API tapılmadı!',
        confirmButtonColor: '#ef4444'
      });
      setIsSubmitting(false);
      return;
    }

    console.log('📤 Submission started:', {
      category: formValues.subCategoryId,
      images: formValues.images.length,
      apiPath: apiPath
    });

    // ✅ IMPROVED: Better FormData construction
    const formData = new FormData();
    
    // Add form fields
    formData.append("UserId", user.userId);
    formData.append("SubCategoryId", formValues.subCategoryId);
    formData.append("Name", formValues.name);
    formData.append("CityId", formValues.cityId);
    formData.append("Price", formValues.price);
    formData.append("Description", formValues.description);
    formData.append("EndDate", formValues.endDate);

    // ✅ CRITICAL: Add images with proper naming
    formValues.images.forEach((image, index) => {
      console.log(`📎 Adding image ${index}:`, image.name, image.size, image.type);
      
      // Use proper File object with correct name
      const fileName = `image_${index}.jpg`;
      formData.append("Images", image, fileName);
    });

    // Add additional attributes
    Object.entries(formValues.additionalAttributes).forEach(([key, value]) => {
      console.log(`📋 Adding attribute:`, key, value);
      formData.append(key, value);
    });

    // ✅ Log FormData contents
    console.log('📊 FormData contents:');
    for (let [key, value] of formData) {
      if (value instanceof File) {
        console.log(`  ${key}: [File] ${value.name} (${value.size} bytes, ${value.type})`);
      } else if (value instanceof Blob) {
        console.log(`  ${key}: [Blob] (${value.size} bytes)`);
      } else {
        console.log(`  ${key}: ${value}`);
      }
    }

    const loadingToast = toast.loading('🚀 Elan yerləşdirilir...');

    try {
      // ✅ Send with proper headers
      const response = await axiosInstance.post(apiPath, formData, {
        headers: {
          // ✅ DO NOT set Content-Type manually
          // axios will set it with proper boundary
        }
      });

      toast.dismiss(loadingToast);

      console.log('✅ Response:', response.status, response.data);

      if (response.status === 200 || response.status === 201) {
        localStorage.removeItem('draftAuction');

        Swal.fire({
          icon: 'success',
          title: '🎉 Uğurlu!',
          text: 'Elanınız uğurla yerləşdirildi!',
          confirmButtonText: 'Bağla',
          confirmButtonColor: '#10b981'
        }).then(() => {
          navigate('/');
        });
      }
    }  catch (error) {
  toast.dismiss(loadingToast);
  console.error('❌ Elan yerləşdirmə xətası:', error);
  console.error('❌ Response status:', error.response?.status);
  console.error('❌ Response data:', error.response?.data);

  let errorText = 'Elan yerləşdirilə bilmədi. Yenidən cəhd edin.';

  if (error.response?.status === 401) {
    errorText = 'Sessiya bitmişdir. Yenidən giriş edin.';
  } else if (error.response?.status === 400) {
    // ✅ ƏLAVƏ ET: Tam validation error-ları göstər
    const validationErrors = error.response.data?.errors;
    if (validationErrors) {
      console.error('❌ Validation errors:', JSON.stringify(validationErrors, null, 2));
      
      // Xətaları siyahı şəklində göstər
      const errorMessages = Object.entries(validationErrors)
        .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
        .join('\n');
      
      console.error('❌ Error messages:\n', errorMessages);
      errorText = `Xətalar:\n${errorMessages}`;
    } else {
      errorText = error.response.data?.message || 'Göndərilən məlumatlar düzgün deyil.';
    }
    console.error('❌ Backend validation errors:', error.response.data);
  } else if (error.response?.status === 500) {
    errorText = 'Server xətası. Zəhmət olmasa sonra cəhd edin.';
    console.error('❌ Server error details:', error.response.data);
  }

  Swal.fire({
    icon: 'error',
    title: 'Xəta!',
    text: errorText,
    confirmButtonColor: '#ef4444'
  });
} finally {
  setIsSubmitting(false);
}

  };

  const handleNewAuctionClick = async (e) => {
    e.preventDefault();

    if (!user) {
      setShake(true);
      toast.warn("⚠️ Elan yerləşdirmək üçün giriş etməlisiniz!", {
        autoClose: 3000
      });

      setTimeout(() => {
        navigate('/giris');
      }, 2000);

      setTimeout(() => setShake(false), 500);
      return;
    }

    if (calculateProgress() < 100) {
      toast.error('❌ Zəhmət olmasa bütün sahələri doldurun!');
      return;
    }

    const confirmed = await Swal.fire({
      title: "⚠️ Əminsiniz?",
      html: `
        <div style="text-align: left; padding: 16px;">
          <p>Daxil etdiyiniz məlumatların düzgünlüyündən əmin olun.</p>
          <p style="color: #f59e0b; margin-top: 12px;">
            <strong>Diqqət:</strong> Hərrac başladıqdan sonra məlumatları dəyişmək mümkün deyil.
          </p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Bəli, yerləşdir",
      cancelButtonText: "Ləğv et",
      confirmButtonColor: '#6366f1',
      cancelButtonColor: '#6b7280'
    });

    if (!confirmed.isConfirmed) return;

    newAuctionPost(e);
  };

  // ============ PREVIEW MODAL ============

  const PreviewModal = () => {
    if (!showPreview) return null;

    return (
      <div className="preview-modal-overlay" onClick={() => setShowPreview(false)}>
        <div className="preview-modal-content" onClick={(e) => e.stopPropagation()}>
          <button className="modal-close" onClick={() => setShowPreview(false)}>
            ✕
          </button>

          <h2 className="preview-title">👁️ Elan Önizləməsi</h2>

          <div className="preview-card">
            {formValues.images.length > 0 ? (
              <div className="preview-images">
                <img src={formValues.imageUrls[0]} alt="Preview" />
                {formValues.images.length > 1 && (
                  <div className="preview-image-count">
                    +{formValues.images.length - 1} şəkil
                  </div>
                )}
              </div>
            ) : (
              <div className="preview-no-image">
                📷 Şəkil yoxdur
              </div>
            )}

            <div className="preview-details">
              <h3 className="preview-name">{formValues.name || 'Elan adı'}</h3>

              <div className="preview-price-section">
                <span className="preview-price-label">Başlanğıc qiymət</span>
                <span className="preview-price">{formValues.price || '0'} AZN</span>
              </div>

              <div className="preview-info-grid">
                <div className="preview-info-item">
                  <span className="preview-info-icon">📍</span>
                  <span>{citiesData.find(c => c.id === parseInt(formValues.cityId))?.name || 'Şəhər'}</span>
                </div>
                <div className="preview-info-item">
                  <span className="preview-info-icon">⏰</span>
                  <span>
                    {timeDifferences.days ? `${timeDifferences.days} gün ` : ''}
                    {timeDifferences.hours ? `${timeDifferences.hours} saat` : ''}
                  </span>
                </div>
              </div>

              <div className="preview-description-section">
                <h4>Təsvir</h4>
                <p className="preview-description">{formValues.description || 'Təsvir əlavə edilməyib'}</p>
              </div>

              {Object.keys(formValues.additionalAttributes).length > 0 && (
                <div className="preview-attributes">
                  <h4>Əlavə məlumatlar</h4>
                  <div className="preview-attributes-grid">
                    {Object.entries(formValues.additionalAttributes).map(([key, value]) => (
                      <div key={key} className="preview-attribute-item">
                        <span className="preview-attribute-key">{key}:</span>
                        <span className="preview-attribute-value">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============ RENDER ============

  return (
    <div className={`new-auction-container ${isDarkMode ? 'dark-mode' : ''}`}>
      <button
        className="dark-mode-toggle-new-auction"
        onClick={() => setIsDarkMode(!isDarkMode)}
        aria-label="Dark mode toggle"
      >
        {isDarkMode ? '☀️' : '🌙'}
      </button>

      {user && (
        <div className="auction-balance glassmorphism">
          <span className="balance-label">Balansınız:</span>
          <strong className="balance-amount">{balance} AZN</strong>
          <a href="/kabinetim/balans" className="balance-link">Balans artır →</a>
        </div>
      )}

      <div className="progress-container glassmorphism">
        <div className="progress-header">
          <span className="progress-text">{calculateProgress()}% tamamlandı</span>
          {autoSaveStatus && (
            <span className="auto-save-status">💾 Son saxlanma: {autoSaveStatus}</span>
          )}
        </div>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${calculateProgress()}%` }}
          ></div>
        </div>
      </div>

      <div className="auction-warning glassmorphism">
        <span className="warning-icon">💡</span>
        <span className="warning-text">
          <strong>Qeyd:</strong> Hərrac yerləşdirmək üçün cəmi <strong>2 AZN</strong> xidmət haqqı tətbiq olunur.
        </span>
      </div>

      <div className="auction-header">
        <h1 className="auction-title">✨ Yeni Hərrac Yarat</h1>
        <p className="auction-subtitle">Məhsulunuzu minlərlə alıcıya təqdim edin</p>
      </div>

      {savedTemplates.length > 0 && (
        <div className="templates-section glassmorphism">
          <div className="templates-header">
            <h4>📁 Şablonlarınız</h4>
            <span className="templates-count">{savedTemplates.length}</span>
          </div>
          <div className="templates-grid">
            {savedTemplates.map(template => (
              <div key={template.id} className="template-card">
                <button
                  onClick={() => loadTemplate(template.id)}
                  className="template-btn"
                >
                  <span className="template-name">{template.name}</span>
                  <span className="template-date">
                    {new Date(template.createdAt).toLocaleDateString('az-AZ')}
                  </span>
                </button>
                <button
                  onClick={(e) => deleteTemplate(template.id, e)}
                  className="template-delete"
                  aria-label="Şablonu sil"
                >
                  🗑️
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {imageUploadStatus && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '16px',
          backgroundColor: '#f3f4f6',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          {imageUploadStatus}
        </div>
      )}

      <div className="auction-layout">
        <div className="form-section">
          <form>
            <div className="form-body">
              <div className="form-group glassmorphism">
                <label className="form-label">
                  <span>Kateqoriya</span>
                  <span className="required">*</span>
                  {formValidation.subCategoryId && <span className="valid-check">✓</span>}
                  <span className="tooltip-icon" title="Məhsulunuz üçün uyğun kateqoriya seçin">ℹ️</span>
                </label>
                <select
                  name="subCategoryId"
                  value={formValues.subCategoryId}
                  onChange={handleInputChange}
                  className="modern-select"
                >
                  <option value="">Kateqoriya seçin</option>
                  {categoriesData?.map((cat) => (
                    <optgroup label={cat.name} key={cat.id}>
                      {cat.subCategories?.map((subCat) => (
                        <option key={subCat.id} value={subCat.id}>
                          {subCat.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              {memoizedSubcategoryForm}

              {formValues.subCategoryId && (
                <>
                  <div className="form-group glassmorphism">
                    <label className="form-label">
                      <span>Hərracın adı</span>
                      <span className="required">*</span>
                      {formValidation.name && <span className="valid-check">✓</span>}
                      <span className="tooltip-icon" title="Minimum 5 simvol">ℹ️</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formValues.name}
                      onChange={handleInputChange}
                      className="modern-input"
                      placeholder="Məsələn: 2020 Toyota Camry Premium"
                    />
                    {formValues.name && formValues.name.length < 5 && (
                      <small className="error-text">Minimum 5 simvol olmalıdır</small>
                    )}
                  </div>

                  <div className="form-group glassmorphism">
                    <label className="form-label">
                      <span>Şəhər</span>
                      <span className="required">*</span>
                      {formValidation.cityId && <span className="valid-check">✓</span>}
                    </label>
                    <select
                      name="cityId"
                      value={formValues.cityId}
                      onChange={handleInputChange}
                      className="modern-select"
                    >
                      <option value="">Şəhər seçin</option>
                      {citiesData?.map((city) => (
                        <option key={city.id} value={city.id}>{city.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group glassmorphism">
                    <label className="form-label">
                      <span>Başlanğıc qiymət</span>
                      <span className="required">*</span>
                      {formValidation.price && <span className="valid-check">✓</span>}
                      <button
                        type="button"
                        className="market-price-btn"
                        onClick={fetchMarketPrices}
                        disabled={isLoadingPrices}
                      >
                        {isLoadingPrices ? '⏳' : '💰'} Bazar qiymətləri
                      </button>
                    </label>
                    <div className="price-input-wrapper">
                      <input
                        type="number"
                        name="price"
                        min="0"
                        step="0.01"
                        value={formValues.price}
                        onChange={handleInputChange}
                        className="modern-input"
                        placeholder="0.00"
                      />
                      <span className="currency-label">AZN</span>
                    </div>
                  </div>

                  <div className="form-group glassmorphism">
                    <label className="form-label">
                      <span>Ətraflı təsvir</span>
                      <span className="required">*</span>
                      {formValidation.description && <span className="valid-check">✓</span>}
                      <span className="tooltip-icon" title="Minimum 20 simvol">ℹ️</span>
                    </label>
                    <textarea
                      name="description"
                      cols="30"
                      rows="6"
                      value={formValues.description}
                      onChange={handleInputChange}
                      className="modern-textarea"
                      placeholder="Məhsulunuz haqqında ətraflı məlumat yazın..."
                    ></textarea>
                    <small className={formValues.description.length >= 20 ? 'success-text' : 'error-text'}>
                      {formValues.description.length}/20 simvol (minimum)
                    </small>
                  </div>

                  <div className="form-group glassmorphism datepicker-wrapper">
                    <label className="form-label">
                      <span>Son tarix</span>
                      <span className="required">*</span>
                      {formValidation.endDate && <span className="valid-check">✓</span>}
                    </label>
                    <DatePicker
                      selected={selectedDate}
                      placeholderText="Tarixi seçin"
                      minDate={minDate}
                      maxDate={maxDate}
                      dateFormat="dd.MM.yyyy HH:mm"
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      timeCaption="Saat"
                      name="endDate"
                      onChange={handleDateChange}
                      filterTime={filterPassedTime}
                      className="modern-datepicker"
                      calendarClassName="custom-calendar"
                    />
                    {formValidation.endDate && (
                      <p className="time-remaining">
                        ⏰ Qalan vaxt:
                        {timeDifferences.days > 0 && ` ${timeDifferences.days} gün`}
                        {timeDifferences.hours > 0 && ` ${timeDifferences.hours} saat`}
                        {timeDifferences.minutes > 0 && ` ${timeDifferences.minutes} dəqiqə`}
                      </p>
                    )}
                  </div>

                  <div className="form-group glassmorphism">
                    <label className="form-label">
                      <span>Şəkillər</span>
                      <span className="required">*</span>
                      {formValidation.images && <span className="valid-check">✓</span>}
                      <span className="image-count-badge">
                        {formValues.images.length}/8
                      </span>
                    </label>

                    <div className="image-upload-container">
                      <label htmlFor="images" className="image-upload-label">
                        {imageProcessing ? (
                          <div className="image-processing">
                            <div className="spinner"></div>
                            <span>Şəkillər optimallaşdırılır...</span>
                          </div>
                        ) : (
                          <>
                            <span className="upload-icon">📷</span>
                            <span className="upload-text">Şəkil seçin və ya buraya sürükləyin</span>
                            <small className="upload-hint">
                              Maksimum 8 şəkil • JPG, PNG, WebP • Hər biri 5MB-dan az
                            </small>
                          </>
                        )}
                      </label>
                      <input
                        ref={imageInputRef}
                        type="file"
                        name="images"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        multiple
                        id="images"
                        onChange={handleImageChange}
                        style={{ display: 'none' }}
                        disabled={imageProcessing || formValues.images.length >= 8}
                      />
                    </div>

                    {formValues.images.length > 0 && (
                      <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="images" direction="horizontal">
                          {(provided, snapshot) => (
                            <div
                              className={`image-preview-grid ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                            >
                              {formValues.imageUrls.map((url, index) => (
                                <Draggable
                                  key={`image-${index}`}
                                  draggableId={`image-${index}`}
                                  index={index}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      className={`image-preview-item ${snapshot.isDragging ? 'dragging' : ''}`}
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                    >
                                      <img
                                        src={url}
                                        alt={`Preview ${index + 1}`}
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover',
                                          borderRadius: '8px'
                                        }}
                                        onError={(e) => {
                                          console.error(`❌ Failed to load image ${index}:`, url);
                                          e.target.style.display = 'none';
                                        }}
                                        onLoad={() => {
                                          console.log(`✅ Image ${index} loaded successfully`);
                                        }}
                                      />
                                      <div className="image-overlay">
                                        <button
                                          type="button"
                                          className="image-delete-btn"
                                          onClick={() => removeImage(index)}
                                          aria-label="Şəkli sil"
                                        >
                                          🗑️
                                        </button>
                                        {index === 0 && (
                                          <span className="primary-badge">Əsas</span>
                                        )}
                                      </div>
                                      <div className="drag-indicator">⋮⋮</div>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    )}
                  </div>
                </>
              )}

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowPreview(true)}
                  disabled={calculateProgress() < 50}
                >
                  👁️ Önizləmə
                </button>

                <button
                  type="button"
                  className="btn-secondary"
                  onClick={saveAsTemplate}
                  disabled={!formValues.name}
                >
                  💾 Şablon saxla
                </button>

                <button
                  className={`btn-primary ${shake ? 'shake' : ''}`}
                  onClick={handleNewAuctionClick}
                  disabled={calculateProgress() < 100 || isSubmitting}
                >
                  {isSubmitting ? '⏳ Gönderiliyor...' : '🚀 Elan Yerləşdir'}
                </button>
              </div>
            </div>
          </form>
        </div>

        {formValues.subCategoryId && (
          <div className="preview-section glassmorphism">
            <h3 className="preview-section-title">👁️ Canlı Önizləmə</h3>
            <div className="live-preview-card">
              {formValues.images.length > 0 ? (
                <div className="live-preview-image-wrapper">
                  <img
                    src={formValues.imageUrls[0]}
                    alt="Live preview"
                    className="live-preview-image"
                    onError={(e) => {
                      console.error('❌ Live preview image failed to load');
                      e.target.style.display = 'none';
                    }}
                    onLoad={() => {
                      console.log('✅ Live preview image loaded');
                    }}
                  />
                  {formValues.images.length > 1 && (
                    <div className="live-preview-image-count">
                      +{formValues.images.length - 1}
                    </div>
                  )}
                </div>
              ) : (
                <div className="live-preview-placeholder">
                  <span className="placeholder-icon">📷</span>
                  <span>Şəkil əlavə edin</span>
                </div>
              )}
              <div className="live-preview-content">
                <h4 className="live-preview-name">
                  {formValues.name || 'Hərrac adı'}
                </h4>
                <p className="live-preview-price">
                  {formValues.price ? `${formValues.price} AZN` : '0 AZN'}
                </p>
                <p className="live-preview-description">
                  {formValues.description
                    ? formValues.description.substring(0, 100) + (formValues.description.length > 100 ? '...' : '')
                    : 'Təsvir...'}
                </p>
                <div className="live-preview-footer">
                  <span className="live-preview-location">
                    📍 {citiesData.find(c => c.id === parseInt(formValues.cityId))?.name || 'Şəhər'}
                  </span>
                  {formValidation.endDate && (
                    <span className="live-preview-time">
                      ⏰ {timeDifferences.days || 0}g {timeDifferences.hours || 0}s
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <PreviewModal />

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDarkMode ? "dark" : "light"}
        limit={3}
      />
    </div>
  );
}

export default NewAuction;